import { useEffect, useRef } from 'react';
import cn from 'classnames';
import { useDelayedUnmount } from 'hooks/utils/useDelayedUnmount';
import styles from './slidePanel.module.scss';

const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getTabbables = panel =>
  [...panel.querySelectorAll(TABBABLE_SELECTOR)].filter(el => el.offsetParent !== null);

// Right-side slide-in. `nested` panels position absolutely inside a parent
// SlidePanel and slide over it (salon / gift choice).
const SlidePanel = ({ isOpen, onClose, nested, centered, ariaLabel, className, children }) => {
  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [display, slidIn] = useDelayedUnmount(isOpen);

  useEffect(() => {
    const panel = panelRef.current;
    if (!slidIn || !panel) return undefined;

    const restoreFocusTo = document.activeElement;
    panel.focus({ preventScroll: true });

    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      e.stopPropagation();
      const tabbables = getTabbables(panel);
      if (!tabbables.length) {
        e.preventDefault();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', handleKeyDown);
    return () => {
      panel.removeEventListener('keydown', handleKeyDown);
      const active = document.activeElement;
      if ((panel.contains(active) || active === document.body) && restoreFocusTo?.isConnected) {
        restoreFocusTo.focus();
      }
    };
  }, [slidIn]);

  return (
    <>
      {!nested && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className={cn(styles.overlay, { [styles.visible]: isOpen })} onClick={onClose} />
      )}
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          styles.panel,
          {
            [styles.opened]: slidIn,
            [styles.nested]: nested,
            [styles.centered]: centered,
            [styles.displayNone]: !display,
          },
          className
        )}
        role="dialog"
        aria-label={ariaLabel}
        aria-modal="true"
      >
        <div
          data-announcer
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        />
        {children}
      </div>
    </>
  );
};

export default SlidePanel;
